import { BrowserRouter, Route, Routes } from "react-router-dom";
import { FC, lazy, Suspense } from "react";
import { ChakraProvider, Box, Text, Flex } from "@chakra-ui/react";
import { HelmetProvider } from "react-helmet-async";
import { ROUTES, resourcesLinks } from "./routes";
import { LayoutWrapper } from "../presentation/components";
import chakraTheme from "../presentation/assets/theme";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../presentation/assets/retro.css";

const HomePageController = lazy(() => import("./pages/HomePageController"));
const ContactPageController = lazy(
  () => import("./pages/ContactPageController")
);
const MePageController = lazy(() => import("./pages/MePageController"));
const ProjectsPageController = lazy(
  () => import("./pages/ProjectsPageController")
);
const CoverGeneratorPageController = lazy(
  () => import("./pages/CoverGeneratorPageController")
);

// 🌟 RETRO 90s APP CONTROLLER 🌟
// Best viewed in Netscape Navigator 4.0!

// Retro Loading Screen
const RetroLoader = () => (
  <Box
    minH="100vh"
    bg="#000080"
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexDirection="column"
    backgroundImage="radial-gradient(white, rgba(255,255,255,.2) 2px, transparent 3px)"
    backgroundSize="100px 100px"
  >
    <Box
      bg="linear-gradient(180deg, #C0C0C0, #808080)"
      border="3px outset #FFFFFF"
      p={0}
      minW="300px"
    >
      {/* Window Title Bar */}
      <Flex
        bg="linear-gradient(90deg, #000080, #1084d0)"
        px={2}
        py={1}
        justify="space-between"
        align="center"
      >
        <Text fontFamily="'Comic Sans MS', cursive" fontWeight="bold" fontSize="sm" color="white">
          ⏳ Loading...
        </Text>
        <Flex gap={1}>
          <Box w="12px" h="12px" bg="#C0C0C0" border="1px outset white" textAlign="center" lineHeight="10px" fontSize="8px">_</Box>
          <Box w="12px" h="12px" bg="#C0C0C0" border="1px outset white" textAlign="center" lineHeight="10px" fontSize="8px">□</Box>
          <Box w="12px" h="12px" bg="#C0C0C0" border="1px outset white" textAlign="center" lineHeight="10px" fontSize="8px">✕</Box>
        </Flex>
      </Flex>

      <Box p={4} textAlign="center">
        <Text fontFamily="'Comic Sans MS', cursive" color="black" mb={3}>
          🚀 Loading AWESOME content... 🚀
        </Text>
        
        {/* Retro Progress Bar */}
        <Box
          bg="#C0C0C0"
          border="2px inset #808080"
          h="20px"
          overflow="hidden"
        >
          <Box
            bg="linear-gradient(90deg, #000080, #0000FF)"
            h="100%"
            sx={{
              animation: "loading-bar 2s infinite",
              "@keyframes loading-bar": {
                "0%": { width: "0%" },
                "50%": { width: "80%" },
                "100%": { width: "100%" },
              },
            }}
          />
        </Box>
        
        <Text fontFamily="'VT323', monospace" color="#000080" mt={2} fontSize="sm">
          Please wait...
        </Text>
      </Box>
    </Box>
  </Box>
);

export const AppController: FC = () => {
  return (
    <HelmetProvider>
      <ChakraProvider theme={chakraTheme}>
        <BrowserRouter>
          <Routes>
            <Route
              path={ROUTES.home}
              element={
                <Suspense fallback={<RetroLoader />}>
                  <HomePageController />
                </Suspense>
              }
            />
            <Route
              path="/"
              element={<LayoutWrapper resourcesLinks={resourcesLinks} />}
            >
              <Route
                path={ROUTES.me}
                element={
                  <Suspense fallback={<RetroLoader />}>
                    <MePageController />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.projects}
                element={
                  <Suspense fallback={<RetroLoader />}>
                    <ProjectsPageController />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.contact}
                element={
                  <Suspense fallback={<RetroLoader />}>
                    <ContactPageController />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.cover}
                element={
                  <Suspense fallback={<RetroLoader />}>
                    <CoverGeneratorPageController />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path="*"
              element={
                <Suspense fallback={<RetroLoader />}>
                  <HomePageController />
                </Suspense>
              }
            />
          </Routes>
        </BrowserRouter>
      </ChakraProvider>
    </HelmetProvider>
  );
};
